import React, { useId, useState } from 'react';

import ChevronIcon from '@/assets/chevron.svg?react';
import Cross from '@/assets/cross.svg?react';
import OptionItem from '@/components/ui/ComboBox/OptionItem';
import { selectClasses } from '@/components/ui/Select/Select';
import cn from '@/utils/cn';

import { MultiSelectItem } from '../MultiSelect';
import { ScrollArea } from '../Select/ScrollArea';
import { sortArrayByQuery } from './utils/sortArrayByQuery';
import { Combobox as PrimitiveComboBox } from '@headlessui/react';
import { Float } from '@headlessui-float/react';
import { Trans, useLingui } from '@lingui/react';
import { FieldValues, useFormContext } from 'react-hook-form';

type Option = {
  value: string;
  label: string;
  group?: string;
};

export interface MultiComboBoxProps
  extends React.ComponentPropsWithRef<'input'> {
  field: FieldValues;
  options: Option[];
  placeholder: string;
  success?: boolean;
  error?: string;
}

const MultiComboBox: React.FC<MultiComboBoxProps> = ({
  field,
  options,
  placeholder,
  success,
  error,
  className,
}) => {
  const id = useId();
  const ref = React.useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState('');
  const { i18n } = useLingui();

  const methods = useFormContext();

  const otherGroup = i18n.t('Inne');
  const isGrouped = React.useMemo(() => {
    return options.some((item) => item.group);
  }, [options]);

  const filteredItems = React.useMemo(() => {
    return query === ''
      ? options
      : sortArrayByQuery(
          options.filter((item) => {
            return item?.label?.toLowerCase().includes(query.toLowerCase());
          }),
          query
        );
  }, [query, options]);

  const availableOptions = filteredItems.filter(
    (item) =>
      !field.value
        ?.map((v: Option) => v?.label?.toLowerCase())
        .includes(item?.label?.toLowerCase())
  );

  const groupedData = React.useMemo(() => {
    let data = filteredItems;

    const groups = [...new Set(data.map((item) => item.group || otherGroup))];

    if (isGrouped && data.some((item) => !item.group)) {
      data = data
        .map((item) => {
          return {
            ...item,
            group: item.group ?? otherGroup,
          };
        })
        .sort((a, b) => {
          if (a.group === otherGroup) return 1;
          if (b.group === otherGroup) return -1;
          return 0;
        });
    }

    const getDataByGroup = (arr: MultiSelectItem[], group: string) => {
      return arr.filter((item) => item.group === group);
    };

    const groupedData = groups.reduce((acc, group) => {
      return {
        ...acc,
        [group]: getDataByGroup(data, group),
      };
    }, {});
    return { data: groupedData, groups };
  }, [filteredItems, isGrouped, otherGroup]);

  return (
    <>
      <div className={cn('relative w-full flex-col gap-1', className)}>
        <PrimitiveComboBox
          ref={ref}
          as="div"
          className={cn('relative w-full', className)}
          multiple
          {...field}
          onChange={(e) => {
            setQuery('');
            if (
              field.value &&
              field.value
                .map((v: Option) => v?.label?.toLowerCase())
                .includes(query.toLowerCase())
            ) {
              field.onChange([
                ...field.value.filter(
                  (v: Option) => v?.label?.toLowerCase() !== query.toLowerCase()
                ),
              ]);
              return;
            }

            field.onChange([
              ...e.map((val) => {
                if (query && val === query)
                  return { label: query, value: `user_created_${query}` };

                if (typeof val === 'string')
                  return options.find((opt) => opt?.label === val);

                return val;
              }),
            ]);
            methods.trigger(field.name);
          }}
        >
          {({ open }) => (
            <>
              <Float
                as="div"
                placement="bottom"
                enter="transition-all duration-100 ease-in-out"
                enterFrom="opacity-0 -translate-y-3"
                enterTo="opacity-100 translate-y-0"
                leave="transition-none duration-0 ease-in-out"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
                offset={4}
                onHide={() => {
                  setQuery('');
                }}
                portal
                adaptiveWidth
              >
                <PrimitiveComboBox.Button
                  className={cn(
                    'group flex w-full flex-wrap',
                    selectClasses(),
                    'placeholder:text-dark-gray-200',
                    'body text-oxford-blue-500',
                    'py-[9px] pr-20 [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none',
                    success &&
                      'border-green-400 hover:border-green-400 focus:border-green-400 ',
                    open
                      ? 'border-blue-600 hover:border-blue-600 focus:border-blue-600 focus:ring-0'
                      : 'focus:ring-4',
                    'h-full min-h-[52px] justify-start gap-2 group-hover:border-blue-600',
                    !!error && 'border-red-400'
                  )}
                  as="div"
                >
                  {field.value?.map((v: Option) => (
                    <div
                      onClick={(event) => event.stopPropagation()}
                      className="flex w-fit cursor-default items-stretch gap-2 rounded bg-gray-200 text-left"
                      key={v?.label}
                    >
                      <p className="caption-large my-1.5 ml-2.5">{v?.label}</p>
                      <span
                        role="button"
                        tabIndex={0}
                        aria-label="Remove item"
                        onClick={() => {
                          field.onChange([
                            ...field.value.filter(
                              (item: Option) => item.value !== v.value
                            ),
                          ]);
                          methods.trigger(field.name);
                        }}
                        className="flex cursor-pointer items-center rounded px-1 hover:bg-gray-500"
                      >
                        <Cross width={14} className="text-dark-gray-500" />
                      </span>
                    </div>
                  ))}
                  <PrimitiveComboBox.Input
                    placeholder={
                      !field.value || field.value.length === 0
                        ? placeholder
                        : undefined
                    }
                    id={id}
                    className={cn(
                      'mr-4 min-w-0 flex-1 placeholder:text-dark-gray-200 focus-visible:outline-none active:min-w-4'
                    )}
                    autoComplete="off"
                    onChange={(event) =>
                      setQuery(
                        event.target.value.length > 50
                          ? event.target.value.slice(0, 50)
                          : event.target.value
                      )
                    }
                    value={query}
                    onKeyDown={(event) => {
                      const fieldValue = Array.isArray(field.value)
                        ? field.value
                        : [];

                      if (event.key === 'Enter' && !query) {
                        event.preventDefault();
                        return;
                      }

                      if (
                        event.key === 'Enter' &&
                        !fieldValue
                          .map((v: Option) => v?.label?.toLowerCase())
                          .includes(query.toLowerCase())
                      ) {
                        event.preventDefault();
                        field.onChange([
                          ...fieldValue,
                          { label: query, value: `user_created_${query}` },
                        ]);
                        setQuery('');
                      }

                      if (
                        event.key === 'Enter' &&
                        fieldValue
                          .map((v: Option) => v?.label?.toLowerCase())
                          .includes(query.toLowerCase())
                      ) {
                        event.preventDefault();
                        field.onChange([
                          ...fieldValue.filter(
                            (v: Option) =>
                              v?.label?.toLowerCase() !== query.toLowerCase()
                          ),
                        ]);
                        setQuery('');
                      }

                      if (event.key === 'Backspace' && !query) {
                        field.onChange([
                          ...fieldValue.slice(0, fieldValue.length - 1),
                        ]);
                      }
                    }}
                    onBlur={() => {
                      if (
                        query &&
                        availableOptions.length === 1 &&
                        availableOptions[0].label.toLowerCase() ===
                          query.toLowerCase()
                      ) {
                        field.onChange([...field.value, ...availableOptions]);
                        setQuery('');
                      }
                    }}
                  />
                  <div className="absolute right-[41px] top-1/2 flex -translate-y-1/2 cursor-pointer items-center">
                    {field.value?.length > 0 && (
                      <>
                        <button
                          className={cn('p-2.5')}
                          onClick={(e) => {
                            e.preventDefault();
                            field.onChange([]);
                            methods.trigger(field.name);
                          }}
                          data-testid="clear-all"
                        >
                          <Cross width={14} className="text-dark-gray-300" />
                        </button>
                        <div className="h-6 w-[1px] bg-dark-gray-300" />
                      </>
                    )}
                  </div>
                  <div className="absolute right-4 top-1/2 flex -translate-y-1/2 items-center gap-2.5">
                    <div className="flex items-center">
                      <ChevronIcon
                        width={14}
                        height={14}
                        className={cn(
                          'shrink-0 rotate-90 cursor-pointer text-dark-gray-300 transition-transform duration-300 ease-in-out',
                          open && 'rotate-[270deg]'
                        )}
                      />
                    </div>
                  </div>
                </PrimitiveComboBox.Button>

                {isGrouped ? (
                  <PrimitiveComboBox.Options
                    className={cn(
                      'my-0.5 bg-white p-1.5',
                      'w-[var(--radix-select-trigger-width)]',
                      'data-[state=open]:duration-150 data-[state=closed]:ease-in-out data-[state=open]:ease-in-out data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[side=bottom]:slide-in-from-top-2',
                      'rounded-lg border border-gray-200 shadow-shadow14'
                    )}
                  >
                    <ScrollArea mah={'max-h-[220px] sm:max-h-[284px]'}>
                      <div
                        className={cn(
                          'flex flex-col gap-1 border-gray-400',
                          ((query && filteredItems.length > 7) ||
                            (!query && options.length > 7) ||
                            (query &&
                              availableOptions.length +
                                groupedData.groups.filter((grp) =>
                                  availableOptions.find(
                                    (opt) => opt.group === grp
                                  )
                                ).length >
                                7)) &&
                            'pr-3.5'
                        )}
                      >
                        {/* in order to not set first value */}
                        <OptionItem
                          item={{ value: '', label: '' }}
                          key={'empty'}
                          className={cn('hidden')}
                        />

                        {availableOptions.length > 0 &&
                          groupedData.groups
                            .filter((grp) =>
                              availableOptions.some((opt) => opt.group === grp)
                            )
                            .map((group) => (
                              <div key={group} className="flex flex-col gap-1">
                                {(
                                  groupedData.data[
                                    (group ||
                                      otherGroup) as keyof typeof groupedData.data
                                  ] as MultiSelectItem[]
                                )?.filter(
                                  (item) =>
                                    !field.value?.some(
                                      (v: Option) =>
                                        v?.label?.toLowerCase() ===
                                        item?.label?.toLowerCase()
                                    )
                                ).length > 0 && (
                                  <OptionItem
                                    item={
                                      {
                                        label: group,
                                        value: group,
                                      } as MultiSelectItem
                                    }
                                    className={cn(
                                      'caption-large-semi pointer-events-none relative select-none rounded px-2 py-1 uppercase text-oxford-blue-500 transition-colors duration-200 hover:bg-gray-200'
                                    )}
                                  />
                                )}
                                {(
                                  groupedData.data[
                                    (group ||
                                      otherGroup) as keyof typeof groupedData.data
                                  ] as MultiSelectItem[]
                                )
                                  ?.filter(
                                    (item) =>
                                      !field.value?.some(
                                        (v: Option) =>
                                          v?.label?.toLowerCase() ===
                                          item?.label?.toLowerCase()
                                      )
                                  )
                                  .map((item) => (
                                    <OptionItem
                                      item={item}
                                      key={item?.label}
                                      className={cn('pl-3')}
                                      onClick={() => {
                                        setQuery('');
                                      }}
                                    />
                                  ))}
                              </div>
                            ))}
                        {query && availableOptions.length === 0 && (
                          <OptionItem
                            item={{
                              label: query,
                              value: `user_created_${query}`,
                            }}
                            key={'No results found'}
                            className={cn(
                              'transition-colors duration-200 hover:bg-gray-200'
                            )}
                          >
                            {field.value &&
                            field.value
                              .map((v: Option) => v?.label?.toLowerCase())
                              .includes(query.toLowerCase())
                              ? `- ${i18n.t('Usuń')} ${field.value?.find((v: Option) => v?.label?.toLowerCase() === query.toLowerCase())?.label}`
                              : `+ ${i18n.t('Dodaj')} ${query}`}
                          </OptionItem>
                        )}
                        {availableOptions.length === 0 && !query && (
                          <OptionItem
                            item={{
                              label: 'Brak wyników',
                              value: 'none',
                            }}
                            key={'No results found'}
                            className={cn(
                              'pointer-events-none cursor-not-allowed'
                            )}
                          >
                            <span className="text-base leading-6 text-dark-gray-200">
                              <Trans id="Brak opcji">Brak opcji</Trans>
                            </span>
                          </OptionItem>
                        )}
                      </div>
                    </ScrollArea>
                  </PrimitiveComboBox.Options>
                ) : (
                  <PrimitiveComboBox.Options
                    className={cn(
                      'my-1.5 bg-white p-1.5',
                      'w-[var(--radix-select-trigger-width)]',
                      'data-[state=open]:duration-150 data-[state=closed]:ease-in-out data-[state=open]:ease-in-out data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[side=bottom]:slide-in-from-top-2',
                      'rounded-lg border border-gray-200 shadow-shadow14'
                    )}
                  >
                    <ScrollArea mah={'max-h-[220px] sm:max-h-[266px]'}>
                      <div
                        className={cn(
                          'flex flex-col gap-1 border-gray-400',
                          (query && filteredItems.length > 7) ||
                            (!query && options.length > 7 && 'pr-3.5')
                        )}
                      >
                        {/* in order to not set first value */}
                        <OptionItem
                          item={{ value: '', label: '' }}
                          key={'empty'}
                          className={cn('hidden')}
                        />
                        {!query &&
                          options.map((item) => (
                            <OptionItem item={item} key={item?.label} />
                          ))}
                        {query &&
                          filteredItems.map((item) => (
                            <OptionItem item={item} key={item?.label} />
                          ))}
                      </div>
                    </ScrollArea>
                  </PrimitiveComboBox.Options>
                )}
              </Float>

              {error && !success && (
                <div className="mt-3 flex w-[239px] items-center justify-start gap-2">
                  <div>{error}</div>
                </div>
              )}
            </>
          )}
        </PrimitiveComboBox>
      </div>
    </>
  );
};

export default MultiComboBox;
